import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import api from '@/services/api';

interface ConsentPreferences {
  cookiesConsent: boolean;
  marketingConsent: boolean;
  analyticsConsent: boolean;
  functionalConsent: boolean;
  privacyPolicyVersion: string;
  acceptedAt?: Date;
}

interface ConsentContextType {
  preferences: ConsentPreferences | null;
  loading: boolean;
  showBanner: boolean;
  updateConsent: (preferences: Partial<ConsentPreferences>) => Promise<void>;
  hasConsent: (type: keyof ConsentPreferences) => boolean;
  revokeConsent: () => Promise<void>;
  acceptAll: () => Promise<void>;
  rejectNonEssential: () => Promise<void>;
  setShowBanner: (show: boolean) => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

const STORAGE_KEY = 'user_consent_preferences';
const CURRENT_PRIVACY_VERSION = '1.0.0';

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<ConsentPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const { isAuthenticated, token } = useAuth();

  // Carregar preferências do localStorage ao iniciar
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // Primeiro, tentar carregar do localStorage
        const storedPreferences = localStorage.getItem(STORAGE_KEY);

        if (storedPreferences) {
          const parsed = JSON.parse(storedPreferences);
          setPreferences(parsed);

          // Verificar se precisa mostrar o banner
          // Mostra se não tiver consentimento ou se a versão da política mudou
          if (!parsed.cookiesConsent || parsed.privacyPolicyVersion !== CURRENT_PRIVACY_VERSION) {
            setShowBanner(true);
          }
        } else {
          // Não tem preferências salvas, mostrar banner
          setShowBanner(true);
        }

        // Se estiver autenticado, sincronizar com o backend
        if (isAuthenticated && token) {
          try {
            const response = await api.get('/user/consent');
            if (response.data.success && response.data.data.consent) {
              const backendPreferences: ConsentPreferences = {
                cookiesConsent: response.data.data.consent.cookiesConsent,
                marketingConsent: response.data.data.consent.marketingConsent,
                analyticsConsent: response.data.data.consent.analyticsConsent,
                functionalConsent: response.data.data.consent.functionalConsent,
                privacyPolicyVersion: response.data.data.consent.privacyPolicyVersion,
                acceptedAt: response.data.data.consent.acceptedAt
              };

              setPreferences(backendPreferences);
              localStorage.setItem(STORAGE_KEY, JSON.stringify(backendPreferences));

              // Atualizar visibilidade do banner
              if (!backendPreferences.cookiesConsent || backendPreferences.privacyPolicyVersion !== CURRENT_PRIVACY_VERSION) {
                setShowBanner(true);
              } else {
                setShowBanner(false);
              }
            }
          } catch (error: any) {
            // Se der erro na busca do backend, usar localStorage
            // Ignorar erros 401/404 silenciosamente (usuário não autenticado ou sem consentimento)
            if (error?.response?.status !== 401 && error?.response?.status !== 404) {
              console.error('Erro ao buscar consentimentos do backend:', error);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar preferências de consentimento:', error);
        setShowBanner(true);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [isAuthenticated, token]);

  const updateConsent = async (newPreferences: Partial<ConsentPreferences>) => {
    try {
      const updatedPreferences: ConsentPreferences = {
        ...preferences,
        ...newPreferences,
        privacyPolicyVersion: CURRENT_PRIVACY_VERSION,
        acceptedAt: new Date()
      } as ConsentPreferences;

      // Atualizar estado local
      setPreferences(updatedPreferences);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPreferences));

      // Se estiver autenticado, sincronizar com backend
      if (isAuthenticated && token) {
        try {
          await api.patch('/user/consent', updatedPreferences);
        } catch (error) {
          console.error('Erro ao salvar consentimentos no backend:', error);
        }
      }

      // Ocultar banner após salvar
      setShowBanner(false);
    } catch (error) {
      console.error('Erro ao atualizar consentimentos:', error);
      throw error;
    }
  };

  const hasConsent = (type: keyof ConsentPreferences): boolean => {
    if (!preferences) return false;
    return preferences[type] === true;
  };

  const revokeConsent = async () => {
    try {
      const revokedPreferences: ConsentPreferences = {
        cookiesConsent: false,
        marketingConsent: false,
        analyticsConsent: false,
        functionalConsent: false,
        privacyPolicyVersion: CURRENT_PRIVACY_VERSION,
        acceptedAt: new Date()
      };

      setPreferences(revokedPreferences);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(revokedPreferences));

      // Se estiver autenticado, revogar no backend
      if (isAuthenticated && token) {
        try {
          await api.patch('/user/consent/revoke');
        } catch (error) {
          console.error('Erro ao revogar consentimentos no backend:', error);
        }
      }

      setShowBanner(true);
    } catch (error) {
      console.error('Erro ao revogar consentimentos:', error);
      throw error;
    }
  };

  const acceptAll = async () => {
    await updateConsent({
      cookiesConsent: true,
      marketingConsent: true,
      analyticsConsent: true,
      functionalConsent: true
    });
  };

  const rejectNonEssential = async () => {
    await updateConsent({
      cookiesConsent: true, // Essenciais sempre true
      marketingConsent: false,
      analyticsConsent: false,
      functionalConsent: false
    });
  };

  const value: ConsentContextType = {
    preferences,
    loading,
    showBanner,
    updateConsent,
    hasConsent,
    revokeConsent,
    acceptAll,
    rejectNonEssential,
    setShowBanner
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
}
